import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { processCommand } from './CommandProcessor';

const blink = keyframes`
  0%, 50% { opacity: 1; }
  51%, 100% { opacity: 0; }
`;

const TerminalContainer = styled.div`
  background: #000;
  color: #00ff00;
  font-family: 'Courier New', monospace;
  padding: 20px;
  height: 100%;
  overflow-y: auto;
  font-size: 14px;
  line-height: 1.4;
`;

const BootSequence = styled.div`
  margin-bottom: 20px;
  color: #00ff00;
`;

const TerminalOutput = styled.div`
  margin-bottom: 20px;
  white-space: pre-wrap;
`;

const TerminalLine = styled.div`
  margin-bottom: 4px;
  word-wrap: break-word;
`;

const InputLine = styled.div`
  display: flex;
  align-items: center;
`;

const Prompt = styled.span`
  color: #00ff00;
  margin-right: 8px;
  user-select: none;
`;

const Input = styled.input`
  background: transparent;
  border: none;
  color: #00ff00;
  font-family: inherit;
  font-size: inherit;
  outline: none;
  flex: 1;
  caret-color: #00ff00;
`;

const Cursor = styled.span`
  background: #00ff00;
  color: #000;
  animation: ${blink} 1s infinite;
  padding: 0 2px;
`;

const CommandOutput = styled.div<{ type?: 'error' | 'success' | 'info' }>`
  color: ${props => 
    props.type === 'error' ? '#ff0000' :
    props.type === 'success' ? '#00ff00' :
    props.type === 'info' ? '#00ffff' :
    '#cccccc'
  };
  margin: 5px 0;
`;

const TerminalApp: React.FC = () => {
  const [history, setHistory] = useState<Array<{ type: 'command' | 'output'; content: string; outputType?: 'error' | 'success' | 'info' }>>([]);
  const [currentInput, setCurrentInput] = useState('');
  const [commandHistory, setCommandHistory] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [isBooting, setIsBooting] = useState(true);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const bootMessages = [
    'Linux Portfolio Terminal v2.0.1',
    'Initializing cybersecurity modules...',
    'Loading user profile: mukul_joshi',
    'Mounting portfolio filesystem...',
    'Starting network services...',
    'Security protocols activated.',
    'System ready.',
    '',
    'Welcome to Mukul\'s Cybersecurity Portfolio Terminal!',
    'Type "help" for available commands.',
    ''
  ];

  useEffect(() => {
    const bootTimer = setTimeout(() => {
      setIsBooting(false);
      bootMessages.forEach((message, index) => {
        setTimeout(() => {
          setHistory(prev => [...prev, { type: 'output', content: message, outputType: 'info' }]);
        }, index * 200);
      });
    }, 1000);

    return () => clearTimeout(bootTimer);
  }, []);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [history]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const handleCommand = (command: string) => {
    if (command.trim()) {
      const output = processCommand(command.trim());
      
      setHistory(prev => [
        ...prev,
        { type: 'command', content: `$ ${command}` },
        { type: 'output', content: output.content, outputType: output.type }
      ]);

      setCommandHistory(prev => [...prev, command]);
      setHistoryIndex(-1);
    }
    setCurrentInput('');
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleCommand(currentInput);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (commandHistory.length > 0) {
        const newIndex = historyIndex === -1 ? commandHistory.length - 1 : Math.max(0, historyIndex - 1);
        setHistoryIndex(newIndex);
        setCurrentInput(commandHistory[newIndex]);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (historyIndex !== -1) {
        const newIndex = historyIndex + 1;
        if (newIndex >= commandHistory.length) {
          setHistoryIndex(-1);
          setCurrentInput('');
        } else {
          setHistoryIndex(newIndex);
          setCurrentInput(commandHistory[newIndex]);
        }
      }
    } else if (e.key === 'Tab') {
      e.preventDefault();
      // Simple tab completion for common commands
      const commands = ['help', 'ls', 'cd', 'cat', 'whoami', 'skills', 'projects', 'clear', 'hack', 'matrix'];
      const matches = commands.filter(cmd => cmd.startsWith(currentInput.toLowerCase()));
      if (matches.length === 1) {
        setCurrentInput(matches[0]);
      }
    }
  };

  if (isBooting) {
    return (
      <TerminalContainer>
        <BootSequence>
          Booting Mukul's Cybersecurity Terminal...
          <Cursor>█</Cursor>
        </BootSequence>
      </TerminalContainer>
    );
  }

  return (
    <TerminalContainer ref={containerRef} onClick={() => inputRef.current?.focus()}>
      <TerminalOutput>
        {history.map((item, index) => (
          <TerminalLine key={index}>
            {item.type === 'command' ? (
              <span style={{ color: '#00ff00' }}>{item.content}</span>
            ) : (
              <CommandOutput type={item.outputType}>{item.content}</CommandOutput>
            )}
          </TerminalLine>
        ))}
      </TerminalOutput>
      
      <InputLine>
        <Prompt>mukul@portfolio:~$</Prompt>
        <Input
          ref={inputRef}
          value={currentInput}
          onChange={(e) => setCurrentInput(e.target.value)}
          onKeyDown={handleKeyPress}
          autoFocus
        />
      </InputLine>
    </TerminalContainer>
  );
};

export default TerminalApp;
